import React, {
  forwardRef,
  memo,
  useCallback,
  useEffect,
  useImperativeHandle,
  useMemo,
  useRef,
  useState,
} from 'react'
import tw from 'twin.macro'
import { useDispatch } from 'react-redux'
import { MCPayment } from 'src/interfaces/mcPayment'
import { createAsyncAction } from 'src/utils/reduxUtils'
import { FetchMCPayments } from 'src/constants/actionTypes'
import { mcPaymentPageSizeOptions, mcPaymentColumns } from 'src/constants/mc-payment/mcPaymentGrid'
import { IMCPaymentsTableContext, MCPaymentsTableContext } from './MCPaymentsTable.context'
import { GridPaginationModel, GridRowSelectionModel, GridSortModel } from '@mui/x-data-grid-pro'
import { useGridPagination } from 'src/utils/hooks/grid/useGridPagination'
import { useGridColumnPreferences } from 'src/utils/hooks/grid/useGridColumnPreferences'
import { MCPaymentsHeader } from '../grid/MCPaymentsHeader'
import { MCPaymentsItemActionMenu } from '../grid/action/MCPaymentsItemActionMenu'
import { PaymentStatus } from 'src/constants/payment/enums'
import { handleApiError } from 'src/utils/errorHandlers'
import { useLocation } from 'react-router-dom'
import _ from 'lodash'
import { Pagination } from 'src/interfaces/table'
import { GridTable } from 'src/components/shared/grid/GridTable'
import { EmptyMCPayments } from '../grid/MCEmptyPayments'
import { NoteMinor } from '@shopify/polaris-icons'
import { Button } from 'src/components/shared/Button'
import { PaymentsButtons } from 'src/components/payment/PaymentsButtons'

interface FetchMCPaymentsParams {
  limit?: number
  offset?: number
  searchQuery?: string
  tagsFilter?: Array<string>
  sortModel?: GridSortModel
}

const getRowId = (row: MCPayment) => row.id

export interface MCPaymentsTableRef {
  refreshMCPayments: () => Promise<void>
}

interface Props {
  onRefreshPayments: () => Promise<void>
}

const LOCKED_COLUMNS = ['masterContractorExternalId']

const MCPaymentsTableComponent = forwardRef<MCPaymentsTableRef, Props>(
  ({ onRefreshPayments }, ref) => {
    const dispatch = useDispatch()
    const { state } = useLocation()

    const stateFilterRef = useRef<Array<PaymentStatus>>([])

    const { paginationModel, limit, offset, setPaginationModel } =
      useGridPagination(mcPaymentPageSizeOptions)

    const [isLoading, setIsLoading] = useState<boolean>(false)
    const [_payments, setMCPayments] = useState<Array<MCPayment>>([])
    const [totalMCPayments, setTotalMCPayments] = useState<number>(0)
    const [actionAnchor, setActionAnchor] = useState<null | HTMLElement>(null)
    const [paymentToAction, setMCPaymentToAction] = useState<MCPayment | null>(null)
    const [selectedRows, setSelectedRows] = useState<GridRowSelectionModel>([])
    const [searchQuery, setSearchQuery] = useState<string>('')
    const [statusFilter, setStatusFilter] = useState<Array<PaymentStatus>>(
      state?.statusFilter || [],
    )
    const [sortModel, setSortModel] = useState<GridSortModel>([
      { field: 'uploadedAt', sort: 'desc' },
    ])

    const payments = useMemo(() => _.uniqBy(_payments, 'id'), [_payments])

    useEffect(() => {
      if (!state?.statusFilter || _.isEqual(state?.statusFilter, stateFilterRef.current)) {
        return
      }

      stateFilterRef.current = state?.statusFilter
      setStatusFilter(state?.statusFilter)
    }, [state?.statusFilter])

    const selectedMCPayments = useMemo<Array<MCPayment>>(() => {
      return selectedRows.reduce<Array<MCPayment>>((arr, selectedRowId) => {
        const payment = payments.find((item) => item.id === selectedRowId)

        if (payment) {
          arr.push(payment)
        }

        return arr
      }, [])
    }, [payments, selectedRows])

    const fetchMCPayments = useCallback(
      async ({ limit, offset, searchQuery, sortModel }: FetchMCPaymentsParams) => {
        setIsLoading(true)

        try {
          const params: any = {
            limit,
            offset,
            paymentStatus: statusFilter,
          }

          if (searchQuery) {
            params.search = searchQuery
          }

          if (sortModel && sortModel[0]) {
            params.sort = `${sortModel[0].field}:${sortModel[0].sort?.toUpperCase()}`
          }

          const payload: { data: Array<MCPayment>; pagination: Pagination } =
            await createAsyncAction(dispatch, FetchMCPayments.request(params))

          setMCPayments(payload.data)
          setTotalMCPayments(payload.pagination.total)
        } catch (err: unknown) {
          handleApiError(err)
        }

        setIsLoading(false)
      },
      [statusFilter],
    )

    useEffect(() => {
      fetchMCPayments({
        limit,
        offset,
        searchQuery,
        sortModel,
      })
    }, [limit, offset, searchQuery, sortModel, statusFilter])

    const refreshMCPayments = useCallback(async () => {
      await fetchMCPayments({ limit, offset, searchQuery, sortModel })
    }, [limit, offset, searchQuery, sortModel, fetchMCPayments])

    const handleChangePagination = useCallback((paginationModel: GridPaginationModel) => {
      setPaginationModel(paginationModel)
    }, [])

    const openMCPaymentsItemAction = useCallback(
      (event: React.MouseEvent<HTMLButtonElement>, paymentToEdit: MCPayment) => {
        setActionAnchor(event.currentTarget)

        setMCPaymentToAction(paymentToEdit)
      },
      [],
    )

    const closeMCPaymentsItemAction = useCallback(() => {
      setActionAnchor(null)
    }, [])

    const clearMCPaymentsItemAction = useCallback(() => {
      setActionAnchor(null)
      setMCPaymentToAction(null)
    }, [])

    const handleSelectStatus = useCallback((statuses: Array<PaymentStatus>) => {
      setStatusFilter(statuses.slice(statuses.length - 1, statuses.length))
      setSelectedRows([])
    }, [])

    const handleSortModelChange = (newModel: GridSortModel) => {
      setSortModel(newModel)
    }

    const {
      columnVisibilityModel,
      pinnedColumns,
      orderedColumns,
      onColumnVisibilityModelChange,
      onPinnedColumnsChange,
      onColumnOrderChange,
      onToggleColumnVisibility,
      onPinColumn,
      onUnpinColumn,
    } = useGridColumnPreferences({
      tableKey: 'mc-payments',
      columns: mcPaymentColumns,
      lockedColumns: LOCKED_COLUMNS,
    })

    const paymentsTableContextValue = useMemo<IMCPaymentsTableContext>(() => {
      return {
        selectedRows,
        setSelectedRows,

        selectedMCPayments,

        searchQuery,
        setSearchQuery,

        statusFilter,
        setStatusFilter: handleSelectStatus,

        actionAnchor,
        paymentToAction,
        openMCPaymentsItemAction,
        closeMCPaymentsItemAction,
        clearMCPaymentsItemAction,

        columnsConfig: mcPaymentColumns,
        columnVisibilityModel,
        pinnedColumns,
        lockedColumns: LOCKED_COLUMNS,
        onToggleColumnVisibility,
        onColumnVisibilityModelChange,
        onPinColumn,
        onUnpinColumn,
      }
    }, [
      selectedRows,
      setSelectedRows,

      selectedMCPayments,

      searchQuery,
      setSearchQuery,

      statusFilter,
      handleSelectStatus,

      actionAnchor,
      paymentToAction,
      openMCPaymentsItemAction,
      closeMCPaymentsItemAction,
      clearMCPaymentsItemAction,

      columnVisibilityModel,
      pinnedColumns,
      onToggleColumnVisibility,
      onColumnVisibilityModelChange,
      onPinColumn,
      onUnpinColumn,
    ])

    useImperativeHandle(
      ref,
      () => ({
        refreshMCPayments,
      }),
      [refreshMCPayments],
    )

    return (
      <MCPaymentsTableContext.Provider value={paymentsTableContextValue}>
        <div css={[tw`flex items-end w-full justify-end mb-6`]}>
          <a href="/mc_payments_example.csv" download="mc_payments_example.csv">
            <Button
              data-testid="payments-mc-main-download-csv-template-btn"
              className={tw`mr-4`}
              size="small"
              color="white"
              rightIcon={<NoteMinor width={20} height={20} />}
            >
              Download MC CSV template
            </Button>
          </a>
          <PaymentsButtons
            onRefreshPayments={onRefreshPayments}
            onRefreshMCPayments={refreshMCPayments}
            isMCselected
          />
        </div>
        <MCPaymentsHeader />
        <GridTable<MCPayment>
          checkboxSelection
          rows={payments}
          columns={orderedColumns}
          isLoading={isLoading}
          paginationModel={paginationModel}
          pageSizeOptions={mcPaymentPageSizeOptions}
          rowCount={totalMCPayments}
          rowSelectionModel={selectedRows}
          sortModel={sortModel}
          columnVisibilityModel={columnVisibilityModel}
          pinnedColumns={pinnedColumns}
          onColumnVisibilityModelChange={onColumnVisibilityModelChange}
          onPinnedColumnsChange={onPinnedColumnsChange}
          onColumnOrderChange={onColumnOrderChange}
          className={[
            {
              '@media (min-height: 830px)': tw`h-[590px]`,
              '@media (min-height: 1000px)': tw`h-[770px]`,
              '@media (min-height: 1440px)': tw`h-[1170px]`,
              '@media (min-height: 2000px)': tw`h-[1570px]`,
            },
          ]}
          EmptyElement={EmptyMCPayments}
          onSortModelChange={handleSortModelChange}
          onPaginationModelChange={handleChangePagination}
          onRowSelectionModelChange={setSelectedRows}
          getRowId={getRowId}
          tableDataTestId="payments-mc-main-table"
          rowDataTestIdPrefix="payments-mc-main"
        />
        <MCPaymentsItemActionMenu />
      </MCPaymentsTableContext.Provider>
    )
  },
)

export const MCPaymentsTable = memo(MCPaymentsTableComponent)
